import React, { FunctionComponent, useCallback, useEffect } from "react";

import { DataGrid } from "@multiweb/reactComponents";
import { bemClassNames, createSkeletonRowsKeys, DEFAULT_DATA_COUNT_TO_LOAD, EMPTY_ARRAY, getDataRowSibling, invariant, keyGenerator, useInView } from "@multiweb/reactUtils";
import { FetchingState } from "@multiweb/types/react";
import { DataRow } from "../DataRow";
import { EmptyMessage } from "../EmptyMessage";
import { ListHeader } from "../ListHeader";

export type LiteListFrameComponentProps = {
    /** Unikátní klíč pro DataSource. */
    dataSourceKey: string;
    /** Unikátní klíč, který se mění, pokud se ve store změní property, které mohou ovlivnit výšku řádku a musí tedy smazat cache virtualized listu. */
    rowRendererKey?: string;
    /** Seřazené identifikátory dat z DataSource. */
    recordsIds?: ReadonlyArray<string>;
    /** Určuje, zda se mají data načítat postupně při scrollovaní nebo se zobrazit pouze do viditelné části. */
    loadOnScroll?: boolean;
    /** Určuje, zda se aktuálně stahují další data. */
    fetchingState?: FetchingState;
    /** Počet dat, které se právě načítají. */
    fetchingCount?: number;
    /** Určuje, zda na serveru zbývají data k dočtení. */
    hasNextItems?: boolean;
    /** Určuje, zda komponenta může načítat další data. */
    canValueLoad?: boolean;
    /** Určuje, zda se má zobrazit hlavička. */
    showHeader?: boolean;
    /** Funkce, která zajistí načtení dat. */
    loadData: (count: number) => void;
};

const classNames = bemClassNames("lite-list-frame-component");

export const LiteListFrameComponent: FunctionComponent<LiteListFrameComponentProps> = ({
    dataSourceKey,
    rowRendererKey,
    recordsIds = EMPTY_ARRAY,
    loadData,
    fetchingState = FetchingState.initialized,
    fetchingCount = 0,
    hasNextItems = true,
    canValueLoad = true,
    loadOnScroll = true,
    showHeader = false,
}) => {
    const [ivlRef, inView] = useInView();

    useEffect(() => {
        if (loadOnScroll && inView && canValueLoad && fetchingState === FetchingState.initialized) {
            loadData(DEFAULT_DATA_COUNT_TO_LOAD);
        }
    }, [loadData, loadOnScroll, inView, canValueLoad, fetchingState]);

    const canLoadData = hasNextItems && canValueLoad && inView; // Mohu načítat další data pouze pokud je LLF viditelný.

    const noRowsRenderer = useCallback(() => <EmptyMessage dataSourceKey={dataSourceKey} />, [dataSourceKey]);

    const itemRenderer = useCallback(
        (recordId: string) => {
            invariant(recordId, "[LiteListFrameComponent] At least one record has not correct id attribute.");
            return <DataRow {...{ dataSourceKey, recordId, getSibling: getDataRowSibling }} />;
        },
        // `rowRendererKey` je potřebný pro vyčištění cache ve virtualized listu
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dataSourceKey, rowRendererKey],
    );

    return (
        <div ref={ivlRef} className={classNames()}>
            {showHeader && <ListHeader {...{ dataSourceKey }} />}
            <div className={classNames("content")}>
                <DataGrid
                    items={recordsIds}
                    {...{
                        loadOnScroll,
                        fetchingState,
                        fetchingCount,
                        canLoadData,
                        loadData,
                        createSkeletonRowsKeys,
                        keyGenerator,
                        noRowsRenderer,
                        itemRenderer,
                    }}
                />
            </div>
        </div>
    );
};
